/**
 * Analytics utility for tracking custom events across the app
 * Supports both Google Analytics (gtag) and Vercel Analytics
 */

// Declare gtag on window for TypeScript
declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        dataLayer?: any[];
    }
}

/**
 * Track a custom event to Google Analytics
 * @param eventName - Name of the event (e.g., 'login', 'signup', 'message_sent')
 * @param properties - Optional properties to send with the event
 */
export const trackEvent = (
    eventName: string,
    properties?: Record<string, any>
): void => {
    // Google Analytics gtag
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', eventName, properties);
        console.log('📊 [Analytics] Event tracked:', eventName, properties);
    }
};

/**
 * Track page views (automatically handled by gtag, but can be called manually for SPAs)
 * @param pagePath - The path of the page
 * @param pageTitle - Optional page title
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', 'G-90T7CP7610', {
            page_path: pagePath,
            page_title: pageTitle,
        });
    }
};

// Pre-defined event helpers for common actions
export const analytics = {
    // Auth events
    login: (method: 'email' | 'google') => trackEvent('login', { method }),
    signup: (method: 'email' | 'google') => trackEvent('sign_up', { method }),
    logout: () => trackEvent('logout'),

    // Chat events
    messageSent: (chatId?: string) => trackEvent('message_sent', { chat_id: chatId }),
    chatCreated: () => trackEvent('chat_created'),
    chatDeleted: () => trackEvent('chat_deleted'),

    // Engagement events
    elaborateClicked: () => trackEvent('elaborate_clicked'),
    shoppingLinkClicked: (store: string, url: string) =>
        trackEvent('shopping_link_clicked', { store, url }),

    // Onboarding events
    onboardingStarted: () => trackEvent('onboarding_started'),
    onboardingCompleted: () => trackEvent('onboarding_completed'),
    deviceInfoSubmitted: () => trackEvent('device_info_submitted'),
};

export default analytics;
